import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/constants";
import { AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("This reset link is missing a token. Please request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!password || password.length < 6) return setErr("Password must be at least 6 characters.");
    if (password !== confirm) return setErr("Passwords don't match — please re-type them.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail
        || `Reset failed (HTTP ${e?.response?.status || "unknown"}). Request a new link.`);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white rounded-md border border-slate-200 p-8">
        <Link to="/" className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="logo" className="h-10 w-10 object-contain" />
          <div className="font-heading font-black text-slate-900">CLEAVIE'S ENTERPRISE</div>
        </Link>

        {done ? (
          <div data-testid="reset-done" className="text-center space-y-3">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
            <h1 className="font-heading font-black text-2xl text-slate-900">Password updated</h1>
            <p className="text-sm text-slate-600">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="space-y-5">
            <div>
              <div className="tag-overline">Reset password</div>
              <h1 className="font-heading font-black text-2xl text-slate-900 mt-1">Choose a new password</h1>
            </div>

            {err && (
              <div data-testid="reset-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>{err}</div>
              </div>
            )}

            <div>
              <Label className="text-sm font-bold text-slate-900">New password</Label>
              <div className="relative">
                <Input type={showPw ? "text" : "password"} data-testid="reset-password"
                       autoComplete="new-password"
                       value={password} onChange={e => setPassword(e.target.value)} />
                <button type="button" onClick={() => setShowPw(s => !s)} data-testid="reset-pw-toggle"
                        aria-label={showPw ? "Hide" : "Show"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 p-1">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="text-xs text-slate-500 mt-1">At least 6 characters.</div>
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-900">Confirm password</Label>
              <Input type={showPw ? "text" : "password"} data-testid="reset-password-confirm"
                     autoComplete="new-password"
                     value={confirm} onChange={e => setConfirm(e.target.value)} />
            </div>

            <Button type="submit" disabled={busy || !token} data-testid="reset-submit-btn"
                    className="w-full h-11 bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold">
              {busy ? "Updating…" : "Update password"}
            </Button>

            <p className="text-sm text-slate-600 text-center">
              Need a new link? <Link to="/forgot-password" className="font-bold text-[#1D4ED8] hover:underline">Request one</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
